// createItemStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import indexedDBStorage from "./indexedDBStorage";

/**
 * @template T
 * @typedef {Object} ItemStore
 * @property {Object.<string, T>} items
 * @property {boolean} isLoading
 * @property {boolean} hasMore
 * @property {number} page
 * @property {number} pageSize
 * @property {(pagination?: { page?: number, pageSize?: number }, arg?: Array) => Promise<void>} fetchItems
 * @property {(filterFn?: (item: T) => boolean, sortFn?: (a: T, b: T) => number) => T[]} getSortedFiltered
 * @property {() => Promise<void>} refreshItems
 */

/**
 * Create a Zustand store for a list of items
 * @template T
 * @param {string} storeName - Storage key for persistence
 * @param {(item: T) => string} getId - Function to get ID from an item
 * @param {(pagination?: { page?: number, pageSize?: number }) => Promise<{ data: T[], hasMore: boolean }>} fetcher - Async function to fetch items
 * @param {(item: T) => T} [mapFn] - Optional function to map fetched items
 * @returns {import('zustand').UseBoundStore<import('zustand').StoreApi<ItemStore<T>>>}
 */
export function createItemStore(storeName, getId, fetcher, mapFn = (x) => x) {
  return create(
    persist(
      immer((set, get) => ({
        items: {},
        isLoading: false,
        hasMore: true,
        page: 1,
        pageSize: 10,

        /**
         * Fetch items
         * @param {{ page?: number, pageSize?: number }} [options] - Pagination options
         * @param {Array} [arg=[]] - Additional arguments to pass to the fetcher function
         * @returns {Promise<void>}
         */
        async fetchItems(options = {}, arg = []) {
          const currentPage = options.page ?? get().page;
          const currentPageSize = options.pageSize ?? get().pageSize;

          set((state) => {
            state.isLoading = true;
          });

          try {
            const res = await fetcher(arg);

            // More robust response handling
            let rawData = [];
            let hasMore = false;

            if (Array.isArray(res)) {
              rawData = res;
            } else if (res && Array.isArray(res.Data)) {
              rawData = res.Data;
              hasMore = res.hasMore || false;
            } else if (res && typeof res === "object") {
              // Handle single object response by converting to array
              rawData = [res];
            }

            set((state) => {
              rawData.map(mapFn).forEach((item) => {
                const id = getId(item);
                state.items[id] = item;
              });
              state.page = currentPage;
              state.pageSize = currentPageSize;
              state.hasMore = hasMore;
            });
          } catch (error) {
            console.error(`Error in ${storeName} fetchItems:`, error);
            // Optionally set error state
          } finally {
            set((state) => {
              state.isLoading = false;
            });
          }
        },

        addOrUpdateItems(newItems) {
          set((state) => {
            newItems.forEach((item) => {
              const id = getId(item);
              const existing = state.items[id];
              if (
                !existing ||
                JSON.stringify(existing) !== JSON.stringify(item)
              ) {
                state.items[id] = item;
              }
            });
          });
        },

        removeItem(itemId) {
          set((state) => {
            delete state.items[itemId];
          });
        },

        getSortedFiltered(filterFn, sortFn) {
          const items = Object.values(get().items);
          return items
            .filter(filterFn || (() => true))
            .sort(sortFn || (() => 0));
        },

        async refreshItems() {
          set((state) => {
            state.items = {};
            state.page = 1;
            state.hasMore = true;
          });
          await get().fetchItems({ page: 1, pageSize: get().pageSize });
        },
      })),
      {
        name: storeName,
        storage: indexedDBStorage,
        partialize: (state) => ({
          items: state.items,
          pageSize: state.pageSize,
        }),
      }
    )
  );
}
